// Every Scout chat thread the app has open, above the routes (GRO-274).
//
// `ChatProvider` holds one store for the whole app, so a turn keeps streaming while the person is
// on the Board, and the Scout tab and the sidebar both read the same thread. A component reads a
// thread with `threadView` and subscribes for changes; it never owns the turn. Plain data plus
// plain functions, no React, so `chatStore.test.ts` drives it with a fake transport.

import { EMPTY_THREAD, applyFeedEvent } from './chatFeed.ts';
import type { FeedEvent, ThreadView } from './chatFeed.ts';
import type { ChatLine } from './chatThread.ts';
import type { TurnViewBody } from '../api/types.ts';

/** Which conversation: an account's sidebar chat, a Scout-tab conversation, or the admin console's. */
export interface ThreadAddress {
  account_id?: string;
  conversation_id?: string;
}

export interface ConversationSummary {
  conversation_id: string;
  title: string;
  status: string;
  unread: boolean;
  stream_cursor: number;
  updated_at: string;
}

export interface SendReply {
  accepted: boolean;
  reason?: string | null;
  stream_cursor?: number | null;
}

export interface HistoryReply {
  available: boolean;
  conversation: ConversationSummary | null;
  messages: { role: 'user' | 'assistant'; text: string }[];
}

/** One read of the feed: events are handed over as they arrive; it settles when the read ends. */
export interface FeedRead {
  cursor: number | null;
  ended: boolean;
  failed: boolean;
}

/** Everything the store asks of the network. `api/chatTransport.ts` is the real one. */
export interface ChatTransport {
  // `view` says where the person is (the extension's page); the server prepends it as context.
  send(address: ThreadAddress, message: string, view?: TurnViewBody): Promise<SendReply>;
  readFeed(address: ThreadAddress, cursor: number | null, signal: AbortSignal, onEvent: (event: FeedEvent) => void): Promise<FeedRead>;
  stop(address: ThreadAddress): Promise<boolean>;
  history(address: ThreadAddress): Promise<HistoryReply>;
  conversations(): Promise<ConversationSummary[]>;
  seen(address: ThreadAddress): Promise<void>;
}

export interface ChatStore {
  transport: ChatTransport;
  threads: Map<string, ThreadView>;
  addresses: Map<string, ThreadAddress>;
  following: Map<string, AbortController>;
  listeners: Set<() => void>;
  // The thread on screen now, and the threads whose turn ended while it was not.
  viewing: string | null;
  unread: Set<string>;
  conversations: ConversationSummary[];
  // The Scout-tab conversation last open, so returning to the tab reopens it.
  lastConversationId: string | null;
  version: number;
  onTurnEnded: (() => void) | null;
  // The pause before each reconnect after a read that brought nothing; past the last, the follower stops.
  emptyReadDelaysMs: number[];
  // An empty read that lasted this long ran its wait out on a quiet turn (a slow tool): not counted.
  quickReadMs: number;
}

/** Why the composer is locked while an Approve card waits. */
export const ANSWER_CARD_FIRST = 'Answer the card above first. Scout is waiting on it.';

// Reconnect pacing after a failed read, and how many failures in a row before giving up.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];
// After an idle with a send still unanswered, how many times to re-read for its turn.
const QUEUED_TURN_REREADS = 3;
const QUEUED_TURN_PAUSE_MS = 1000;
// A read that ends quickly with no event and no idle: back off, and after three such reconnects stop.
// Scout answers a settled thread at once, so three quick empty reads in a row mean the feed is not
// coming back. A read that ran its wait out (the 20s long-poll, the relay's 10 minutes) is a quiet
// turn, a slow tool, and is never counted.
const EMPTY_READ_DELAYS_MS = [2000, 5000, 10000];
const QUICK_READ_MS = 15000;
export const QUIET_FEED_NOTE = 'Scout stopped sending updates. Reload to catch up.';

export function createChatStore(transport: ChatTransport): ChatStore {
  return {
    transport, threads: new Map(), addresses: new Map(), following: new Map(), listeners: new Set(),
    viewing: null, unread: new Set(), conversations: [], lastConversationId: null, version: 0, onTurnEnded: null,
    emptyReadDelaysMs: EMPTY_READ_DELAYS_MS, quickReadMs: QUICK_READ_MS,
  };
}

/** The store key for a thread, the same three the server derives, under the acting person. */
export function threadKeyFor(actor: string, address: ThreadAddress): string {
  if (address.account_id) return `${actor}:account:${address.account_id}`;
  if (address.conversation_id) return `${actor}:conversation:${address.conversation_id}`;
  return `${actor}:unscoped`;
}

export function subscribeStore(store: ChatStore, listener: () => void): () => void {
  store.listeners.add(listener);
  return () => { store.listeners.delete(listener); };
}

export function threadView(store: ChatStore, key: string): ThreadView {
  return store.threads.get(key) ?? EMPTY_THREAD;
}

function notify(store: ChatStore): void {
  store.version += 1;
  for (const listener of store.listeners) listener();
}

function setThread(store: ChatStore, key: string, view: ThreadView): void {
  store.threads.set(key, view);
  notify(store);
}

function pause(milliseconds: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

/** True when a thread is a Scout-tab conversation, the only kind the nav dot counts. */
function isConversationKey(key: string): boolean {
  return key.includes(':conversation:');
}

/** How many Scout-tab chats finished while the person was elsewhere: the dot on the Scout nav. */
export function unreadConversationCount(store: ChatStore): number {
  const keys = new Set([...store.unread].filter(isConversationKey));
  return keys.size;
}

/** The person has this thread on screen, or none. Opening one clears its dot, here and on the server. */
export function setViewing(store: ChatStore, key: string | null, address?: ThreadAddress): void {
  store.viewing = key;
  if (key !== null && address?.conversation_id) store.lastConversationId = address.conversation_id;
  if (key !== null && store.unread.has(key)) {
    store.unread.delete(key);
    if (address) void store.transport.seen(address).catch(() => undefined);
  }
  notify(store);
}

/** Put lines straight into a thread: the Approve card's decision and the reply it resumed. */
export function appendLines(store: ChatStore, key: string, lines: ChatLine[]): void {
  const view = threadView(store, key);
  setThread(store, key, { ...view, lines: [...view.lines, ...lines] });
}

/** Seed a thread that has no server history yet with lines from this browser (chats before 025). */
export function seedThread(store: ChatStore, key: string, lines: ChatLine[]): void {
  const view = threadView(store, key);
  if (view.lines.length > 0 || lines.length === 0) return;
  setThread(store, key, { ...view, lines });
}

export function clearError(store: ChatStore, key: string): void {
  const view = threadView(store, key);
  if (view.error !== null) setThread(store, key, { ...view, error: null });
}

/** The thread after one feed event, with the dot and the turn-ended hook. */
function receiveEvent(store: ChatStore, key: string, event: FeedEvent): void {
  const before = threadView(store, key);
  const after = applyFeedEvent(before, event);
  store.threads.set(key, after);
  if (event.type === 'turn_ended') {
    // Ended off-screen: a dot. On screen: the relay has just marked it unread, so clear that.
    const address = store.addresses.get(key);
    if (store.viewing !== key) store.unread.add(key);
    else if (address) void store.transport.seen(address).catch(() => undefined);
    store.onTurnEnded?.();
  }
  notify(store);
}

/**
 * Follow a thread's feed from its cursor until it goes idle, reconnecting as needed. One follower
 * per thread, held by the store, so it outlives whatever component started it.
 */
export async function followThread(store: ChatStore, key: string, address: ThreadAddress): Promise<void> {
  if (store.following.has(key)) return;
  const controller = new AbortController();
  store.following.set(key, controller);
  store.addresses.set(key, address);
  let failures = 0;
  let emptyReads = 0;
  let queuedRereads = 0;
  try {
    while (!controller.signal.aborted) {
      // No cursor yet (a chat from before server history): the server starts at the latest event.
      const cursor = threadView(store, key).cursor;
      let sawTurnStart = false;
      let eventCount = 0;
      const readStartedAt = Date.now();
      const read = await store.transport.readFeed(address, cursor, controller.signal, event => {
        eventCount += 1;
        if (event.type === 'turn_started') sawTurnStart = true;
        receiveEvent(store, key, event);
      });
      if (controller.signal.aborted) return;

      // A read that failed: back off and retry, then say so and stop.
      if (read.failed) {
        failures += 1;
        if (failures > RETRY_DELAYS_MS.length) {
          const view = threadView(store, key);
          setThread(store, key, { ...view, running: false, error: view.error || 'Lost the live feed. Reload to catch up.' });
          return;
        }
        await pause(RETRY_DELAYS_MS[failures - 1]!);
        continue;
      }
      failures = 0;

      // Parked on an Approve card: nothing comes until it is answered, so never poll for it.
      if (threadView(store, key).waitingInput) return;

      // A read that ended short of idle: reconnect at once if it brought events, else back off and stop.
      if (!read.ended) {
        const readMs = Date.now() - readStartedAt;
        if (eventCount > 0 || readMs >= store.quickReadMs) {
          emptyReads = 0;
          continue;
        }
        emptyReads += 1;

        // Stopped: reopening the chat loads its history again and re-follows a turn still running.
        if (emptyReads > store.emptyReadDelaysMs.length) {
          const view = threadView(store, key);
          setThread(store, key, {
            ...view, running: false, awaitingTurns: 0, historyLoaded: false, error: view.error || QUIET_FEED_NOTE,
          });
          return;
        }
        await pause(store.emptyReadDelaysMs[emptyReads - 1]!);
        continue;
      }

      // Idle with a send still unanswered: its queued turn may be about to start.
      const view = threadView(store, key);
      if (view.awaitingTurns > 0 && !view.waitingInput && queuedRereads < QUEUED_TURN_REREADS) {
        queuedRereads = sawTurnStart ? 0 : queuedRereads + 1;
        await pause(QUEUED_TURN_PAUSE_MS);
        continue;
      }
      if (view.running) setThread(store, key, { ...view, running: false, awaitingTurns: 0 });
      return;
    }
  } finally {
    store.following.delete(key);
  }
}

/** Load a thread's server history once, and re-attach to it when a turn is still running. */
export async function loadThread(store: ChatStore, key: string, address: ThreadAddress): Promise<void> {
  const existing = threadView(store, key);
  if (existing.historyLoaded) return;
  store.addresses.set(key, address);
  setThread(store, key, { ...existing, historyLoaded: true });
  let history: HistoryReply;
  try {
    history = await store.transport.history(address);
  } catch {
    return;
  }

  // Server history wins over anything seeded, unless the server has none (a chat from before 025).
  const view = threadView(store, key);
  const lines: ChatLine[] = history.messages.length > 0 ? history.messages.map(message => ({ role: message.role, text: message.text })) : view.lines;
  const conversation = history.conversation;
  const running = conversation?.status === 'running';

  // Re-following a turn the feed gave up on: the "stopped sending updates" note no longer holds.
  const error = running && view.error === QUIET_FEED_NOTE ? null : view.error;
  setThread(store, key, {
    ...view, lines, error, cursor: conversation ? conversation.stream_cursor : view.cursor,
    running: running || view.running, awaitingTurns: running ? Math.max(1, view.awaitingTurns) : view.awaitingTurns,
  });
  if (running) void followThread(store, key, address);
}

/** Send a message. It shows at once; the answer streams in. Allowed while a turn runs: it queues. */
export async function sendMessage(store: ChatStore, key: string, address: ThreadAddress, message: string, turnView?: TurnViewBody): Promise<boolean> {
  const before = threadView(store, key);

  // A turn parked on an Approve card never ends until the card is answered, so under
  // turnPolicy "queue" a message sent now would wait silently behind it; "steer" would cancel
  // the parked turn and drop what the card was about to write. Neither is safe: answer first.
  if (before.waitingInput) {
    setThread(store, key, { ...before, error: ANSWER_CARD_FIRST });
    return false;
  }
  setThread(store, key, {
    ...before, lines: [...before.lines, { role: 'user', text: message }], running: true,
    awaitingTurns: before.awaitingTurns + 1, error: null, waitingInput: false,
  });
  let reply: SendReply;
  try {
    reply = await store.transport.send(address, message, turnView);
  } catch (caught) {
    reply = { accepted: false, reason: caught instanceof Error ? caught.message : 'Chat failed.' };
  }
  const view = threadView(store, key);
  if (!reply.accepted) {
    const awaitingTurns = Math.max(0, view.awaitingTurns - 1);
    setThread(store, key, { ...view, awaitingTurns, running: awaitingTurns > 0, error: reply.reason || 'Scout is not available.' });
    return false;
  }

  // Follow from the server's cursor: the oldest unfinished turn, which is this one unless it queued.
  const cursor = typeof reply.stream_cursor === 'number' ? reply.stream_cursor : view.cursor;
  setThread(store, key, { ...view, cursor: store.following.has(key) ? view.cursor : cursor });
  if (address.conversation_id) void refreshConversations(store);
  void followThread(store, key, address);
  return true;
}

/** The Stop button. The feed confirms it with a stopped turn end. */
export async function stopTurn(store: ChatStore, key: string, address: ThreadAddress): Promise<void> {
  let stopped = false;
  try {
    stopped = await store.transport.stop(address);
  } catch {
    stopped = false;
  }
  if (!stopped) {
    const view = threadView(store, key);
    setThread(store, key, { ...view, error: 'Scout did not take the stop. Try again.' });
  }
}

/** The Scout tab's chat list from the server; running chats are re-attached so their dot lands. */
export async function refreshConversations(store: ChatStore, actor?: string): Promise<void> {
  let conversations: ConversationSummary[];
  try {
    conversations = await store.transport.conversations();
  } catch {
    return;
  }
  store.conversations = conversations;
  if (actor !== undefined) {
    for (const conversation of conversations) {
      const key = threadKeyFor(actor, { conversation_id: conversation.conversation_id });
      if (conversation.unread && store.viewing !== key) store.unread.add(key);
      if (conversation.status === 'running' && !store.following.has(key)) {
        void loadThread(store, key, { conversation_id: conversation.conversation_id });
      }
    }
  }
  notify(store);
}

/** Stop every follower: sign-out, or a switch of the person being viewed as. */
export function closeStore(store: ChatStore): void {
  for (const controller of store.following.values()) controller.abort();
  store.following.clear();
}
